import React, { useContext, useMemo } from 'react';

import { createPortal } from 'react-dom';

import { getUnifiedCodeLanguageId } from '../../../utils/getUnifiedCodeLanguageId';
import { ContentContext } from '../../context/ContentContext';
import { PageContext } from '../../context/PageContext';
import { SearchContext } from '../../context/SearchContext';
import { DocSearchModal } from '../DocSearchModal';

interface Props {
  apiKey: string;
  appId: string;
  indexName: string;
}

export const SearchModal: React.FC<Props> = ({ apiKey, appId, indexName }) => {
  const { product, platform, version, search } = useContext(PageContext);
  const { open, closeSearch } = useContext(SearchContext);
  const { activeItems } = useContext(ContentContext);

  const searchParameters = useMemo(() => {
    const facetFilters = [];
    if (product) {
      facetFilters.push(`product:${product}`);
    }
    if (platform) {
      facetFilters.push(`platform:${platform}`);
    }
    if (version) {
      facetFilters.push(`version:${version}`);
    }

    let filterString = '';

    if (search?.filters) {
      // go through the settings and find the first item that matches with a value from the current page settings
      const filterEntry = search.filters.find(({ value }) =>
        Object.values(activeItems).some(
          item =>
            getUnifiedCodeLanguageId(item) === getUnifiedCodeLanguageId(value),
        ),
      );

      if (filterEntry) {
        filterString = filterEntry.related.reduce(
          (arr: string, value, index, self) => {
            const unifiedValue = getUnifiedCodeLanguageId(value);

            return `${arr} OR query:${unifiedValue}<score=${
              self.length - index
            }>`;
          },
          `query:${getUnifiedCodeLanguageId(filterEntry.value)}<score=${
            filterEntry.related.length + 1
          }>`,
        );
      }
    }

    if (!filterString) {
      filterString = Object.values(activeItems).reduce(
        (arr: string, value, index, self) => {
          const unifiedValue = getUnifiedCodeLanguageId(value);

          if (index > 0) {
            return `${arr} OR query:${unifiedValue}<score=${
              self.length - index
            }>`;
          }
          return `query:${unifiedValue}<score=${self.length - index}>`;
        },
        '',
      );
    }

    return {
      filters: filterString.length
        ? `${filterString} OR query:none<score=0>`
        : '',
      optionalFilters: ['query:-none'],
      facetFilters,
    };
  }, [product, platform, version, activeItems, search]);

  if (!open) return null;

  // prevent rerendering this component if it is open, else it will clear the current search input
  return createPortal(
    <DocSearchModal
      appId={appId}
      apiKey={apiKey}
      indexName={indexName}
      onClose={closeSearch}
      initialScrollY={window.scrollY || 0}
      searchParameters={searchParameters}
    />,
    document.body,
  );
};
